import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Pencil, Trash2 } from 'lucide-react';
import {
  AcademicCalendarApi,
  AcademicCalendarEvent,
  AcademicCalendarTerm,
  AcademicCalendarType,
  AcademicCalendarWriteData,
  AcademicYear,
  GenerateAcademicCalendarResult,
  ACADEMIC_CALENDAR_TERMS,
  ACADEMIC_CALENDAR_TYPES,
  AcademicYearsApi,
} from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const columnHelper = createColumnHelper<AcademicCalendarEvent>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

interface EventFormValues {
  name: string;
  description: string;
  type: AcademicCalendarType | '';
  date: string;
  term: AcademicCalendarTerm | '';
  academicYearId: string;
}

const EMPTY_FORM: EventFormValues = { name: '', description: '', type: '', date: '', term: '', academicYearId: '' };

function toWriteData(v: EventFormValues): AcademicCalendarWriteData {
  return {
    name: v.name.trim(),
    description: v.description.trim() || undefined,
    type: v.type as AcademicCalendarType,
    date: v.date,
    term: v.term || undefined,
    academicYearId: v.academicYearId || undefined,
  };
}

/**
 * Over the migrated `academic_calendar` table (151 migrated events: Exam /
 * Teachers Training / Opening Ceremony) plus organically-created ones via
 * the create form below. "Working Day" rows were never migrated in bulk,
 * but real historical ones have since been backfilled per-school from
 * attendance evidence, and the type is now creatable here too — see
 * docs/DEVIATIONS.md. The empty state below covers a school with
 * genuinely zero events without implying anything is broken.
 *
 * "Generate Academic Calendar For A Month" (below the single-day form) —
 * matches real legacy's own generate feature: bulk-creates a "Working Day"
 * row for every SUNDAY (only) in the picked month(s), idempotent. See
 * academic-calendar.service.ts's generateForMonth() doc comment for the
 * full real-source spec this was verified against.
 */
export function AcademicCalendarPage() {
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'date', defaultSortOrder: 'asc' });

  // '' = "All Years". Most migrated events have no academic_year_id at all
  // (year-agnostic Teachers Training / Opening Ceremony rows), so the list
  // query treats a selected year as inclusive (that year's events PLUS every
  // year-agnostic event), never as a strict filter that would hide almost
  // everything. See docs/DEVIATIONS.md.
  const [yearId, setYearId] = useState('');
  const [term, setTerm] = useState('');
  const [month, setMonth] = useState<number | ''>('');
  const [type, setType] = useState('');

  const { data: defaultYear } = useQuery({
    queryKey: ['academic-calendar', 'default-year'],
    queryFn: AcademicCalendarApi.defaultYear,
  });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-calendar-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  useEffect(() => {
    if (defaultYear?.id) setYearId((y) => y || defaultYear.id);
  }, [defaultYear?.id]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['academic-calendar', 'list', dt.params, yearId, term, month, type],
    queryFn: () =>
      AcademicCalendarApi.list({
        ...dt.params,
        academicYearId: yearId || undefined,
        term: term || undefined,
        month: month || undefined,
        type: type || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['academic-calendar', 'list'] });
    qc.invalidateQueries({ queryKey: ['academic-calendar', 'default-year'] });
  }

  // ── Create form (inline, above the list — matching the old system's
  // "Create Academic Calendar For A Day" layout) ──
  const [form, setForm] = useState<EventFormValues>(EMPTY_FORM);
  const create = useMutation({
    mutationFn: () => AcademicCalendarApi.create(toWriteData(form)),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      invalidateAll();
    },
  });

  // ── Edit modal ──
  const [editing, setEditing] = useState<AcademicCalendarEvent | null>(null);
  const [editForm, setEditForm] = useState<EventFormValues>(EMPTY_FORM);
  useEffect(() => {
    if (editing) {
      setEditForm({
        name: editing.name,
        description: editing.description ?? '',
        type: (editing.type as AcademicCalendarType) ?? '',
        date: editing.date,
        term: (editing.term as AcademicCalendarTerm) ?? '',
        academicYearId: editing.academicYearId ?? '',
      });
    }
  }, [editing]);
  const update = useMutation({
    mutationFn: () => AcademicCalendarApi.update(editing!.id, toWriteData(editForm)),
    onSuccess: () => {
      setEditing(null);
      invalidateAll();
    },
  });

  // ── Delete confirm ──
  const [deleting, setDeleting] = useState<AcademicCalendarEvent | null>(null);
  const remove = useMutation({
    mutationFn: () => AcademicCalendarApi.remove(deleting!.id),
    onSuccess: () => {
      setDeleting(null);
      invalidateAll();
    },
  });

  const columns = [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: ctx.row.original.color || undefined }}
          />
          {ctx.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('term', { header: 'Term', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('date', { header: 'Date' }),
    columnHelper.accessor('academicYearName', { header: 'Academic Year', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('addedByName', { header: 'Added By', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => setEditing(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => setDeleting(ctx.row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  return (
    <>
      <PageHeader title="Academic Calendar" description="Exams, teacher trainings, and other calendar events." />

      {/* Create Academic Calendar For A Day */}
      <div className="card mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Create Academic Calendar For A Day</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <Textarea
              rows={1}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <Field label="Type" required>
            <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AcademicCalendarType }))}>
              <option value="">Select Type</option>
              {ACADEMIC_CALENDAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </Field>
          <Field label="Term">
            <Select value={form.term} onChange={(e) => setForm((f) => ({ ...f, term: e.target.value as AcademicCalendarTerm }))}>
              <option value="">Select Term</option>
              {ACADEMIC_CALENDAR_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Session Year">
            <Select value={form.academicYearId} onChange={(e) => setForm((f) => ({ ...f, academicYearId: e.target.value }))}>
              <option value="">Select Session Year</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="mt-3">
          <button
            className="btn-primary"
            disabled={!form.name.trim() || !form.type || !form.date || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Submit'}
          </button>
        </div>
        {create.isSuccess && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Calendar event created.</div>
        )}
        {create.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>
        )}
      </div>

      {/* Generate Academic Calendar For A Month */}
      <GenerateCalendarCard years={years} onGenerated={invalidateAll} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={yearId} onChange={(e) => setYearId(e.target.value)}>
          <option value="">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select className="!w-32" value={term} onChange={(e) => setTerm(e.target.value)}>
          <option value="">All Terms</option>
          {ACADEMIC_CALENDAR_TERMS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select
          className="!w-36"
          value={month}
          onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">All Months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          {ACADEMIC_CALENDAR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        result={pageData}
        state={dt}
        loading={isFetching}
        emptyMessage="No calendar events for the selected filters."
        getRowId={(row) => row.id}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Calendar Event" size="lg">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <Textarea
              rows={1}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <Field label="Type" required>
            <Select
              value={editForm.type}
              onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value as AcademicCalendarType }))}
            >
              <option value="">Select Type</option>
              {ACADEMIC_CALENDAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Field>
          <Field label="Term">
            <Select
              value={editForm.term}
              onChange={(e) => setEditForm((f) => ({ ...f, term: e.target.value as AcademicCalendarTerm }))}
            >
              <option value="">Select Term</option>
              {ACADEMIC_CALENDAR_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Session Year">
            <Select
              value={editForm.academicYearId}
              onChange={(e) => setEditForm((f) => ({ ...f, academicYearId: e.target.value }))}
            >
              <option value="">Select Session Year</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {update.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(update.error)}</div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!editForm.name.trim() || !editForm.type || !editForm.date || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        title="Delete calendar event?"
        message={`This will permanently delete "${deleting?.name}". This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

/** "Generate Academic Calendar For A Month" — see academic-calendar.service.ts's generateForMonth() for the full real-source spec. */
function GenerateCalendarCard({ years, onGenerated }: { years: AcademicYear[]; onGenerated: () => void }) {
  const [academicYearId, setAcademicYearId] = useState('');
  const [months, setMonths] = useState<Set<number>>(new Set());
  const [term, setTerm] = useState<AcademicCalendarTerm | ''>('');

  const generate = useMutation({
    mutationFn: () => AcademicCalendarApi.generate({ months: [...months], term: term as AcademicCalendarTerm, academicYearId }),
    onSuccess: () => {
      setMonths(new Set());
      onGenerated();
    },
  });

  function toggleMonth(m: number) {
    setMonths((prev) => {
      const next = new Set(prev);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });
  }

  const valid = !!academicYearId && months.size > 0 && !!term;

  return (
    <div className="card mb-6 p-4">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Generate Academic Calendar For A Month</h3>
      <p className="mb-3 text-xs text-slate-500">
        Creates a "Working Day" entry for every Sunday in the selected month(s). Already-existing dates are left untouched — safe to run again.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Session Year" required>
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            <option value="">Select Session Year</option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Term" required>
          <Select value={term} onChange={(e) => setTerm(e.target.value as AcademicCalendarTerm)}>
            <option value="">Select Term</option>
            {ACADEMIC_CALENDAR_TERMS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="mt-3">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">
          Month{months.size > 0 ? ` (${months.size} selected)` : ''} <span className="text-red-500">*</span>
        </span>
        <div className="flex flex-wrap gap-2">
          {MONTHS.map((m, i) => {
            const num = i + 1;
            const checked = months.has(num);
            return (
              <label
                key={m}
                className={`cursor-pointer rounded-md border px-2.5 py-1 text-sm transition ${
                  checked ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => toggleMonth(num)} className="sr-only" />
                {m.slice(0, 3)}
              </label>
            );
          })}
        </div>
      </div>
      <div className="mt-3">
        <button className="btn-primary" disabled={!valid || generate.isPending} onClick={() => generate.mutate()}>
          {generate.isPending ? 'Generating…' : 'Submit'}
        </button>
      </div>
      {generate.data && (
        <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          ✓ Created {generate.data.created} working day{generate.data.created === 1 ? '' : 's'}
          {generate.data.skipped > 0 ? ` (${generate.data.skipped} already existed, left unchanged)` : ''}.
        </div>
      )}
      {generate.error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(generate.error)}</div>
      )}
    </div>
  );
}
